<template>
    <header class="conversation-header">
        <button
            v-if="showMenu"
            type="button"
            class="menu-btn"
            aria-label="Открыть меню"
            @click="$emit('toggle-sidebar')"
        >
            <ChatIcon name="list" :size="18" />
        </button>

        <div class="title-wrap">
            <span class="title-icon">
                <ChatIcon name="chat" :size="14" />
            </span>
            <div class="title-text">
                <!--
                    Inline-редактирование. Активируется из пункта меню «Переименовать ветку»
                    или двойным кликом по заголовку — Enter сохраняет, Esc отменяет.
                -->
                <template v-if="isRenaming">
                    <input
                        ref="renameInput"
                        v-model="draftTitle"
                        type="text"
                        class="title-input"
                        :maxlength="120"
                        @keydown.enter.prevent="commitRename"
                        @keydown.esc.prevent="cancelRename"
                        @blur="commitRename"
                    />
                </template>
                <template v-else>
                    <div
                        class="title"
                        :title="title"
                        @dblclick="startRename"
                    >
                        {{ title }}
                    </div>
                    <div class="subtitle">{{ subtitle }}</div>
                </template>
            </div>
        </div>

        <div class="actions">
            <button
                type="button"
                class="action-btn"
                aria-label="Поделиться"
                @click="$emit('share')"
            >
                <ChatIcon name="share" :size="16" />
                <span class="action-label">Поделиться</span>
            </button>
            <!--
                Кнопка «Экспорт» показывается только авторизованным: PDF
                уходит в личный кабинет («Мои файлы»), у гостя его нет.
                Гостю мы не показываем неактивную кнопку — она путала бы
                новичков; вместо этого весь UI экспорта скрыт до логина.
            -->
            <button
                v-if="isAuthenticated"
                type="button"
                class="action-btn"
                aria-label="Экспорт в PDF"
                title="Экспорт в PDF"
                @click="$emit('export')"
            >
                <ChatIcon name="download" :size="16" />
                <span class="action-label">Экспорт</span>
            </button>
            <div class="more-wrap" v-click-outside="() => (menuOpen = false)">
                <button
                    type="button"
                    class="action-icon"
                    aria-label="Дополнительные действия"
                    @click="menuOpen = !menuOpen"
                >
                    <ChatIcon name="more-vertical" :size="18" />
                </button>
                <transition name="dd">
                    <div v-if="menuOpen" class="more-menu" role="menu">
                        <button
                            type="button"
                            class="more-item"
                            @click="onMenuItem('rename')"
                        >
                            <ChatIcon name="edit" :size="13" />
                            Переименовать ветку
                        </button>
                        <button
                            v-if="isAdmin"
                            type="button"
                            class="more-item"
                            @click="onMenuItem('publish')"
                        >
                            <ChatIcon name="shield" :size="13" />
                            Опубликовать как готовый отчёт
                        </button>
                        <div class="more-divider" />
                        <button
                            type="button"
                            class="more-item more-item--danger"
                            @click="onMenuItem('delete')"
                        >
                            <ChatIcon name="trash" :size="13" />
                            Удалить ветку
                        </button>
                    </div>
                </transition>
            </div>
        </div>
    </header>
</template>

<script setup>
import { nextTick, ref } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'

const props = defineProps({
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    showMenu: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    // Управляет видимостью экспортной кнопки (см. комментарий в шаблоне).
    isAuthenticated: { type: Boolean, default: false },
})

const emit = defineEmits(['share', 'export', 'toggle-sidebar', 'publish', 'rename', 'delete-thread'])

const menuOpen = ref(false)
const isRenaming = ref(false)
const draftTitle = ref('')
const renameInput = ref(null)

async function startRename() {
    draftTitle.value = props.title || ''
    isRenaming.value = true
    await nextTick()
    const el = renameInput.value
    if (el) {
        el.focus()
        el.select()
    }
}

function commitRename() {
    if (!isRenaming.value) return
    const next = (draftTitle.value || '').trim()
    isRenaming.value = false
    if (!next || next === props.title) return
    emit('rename', next)
}

function cancelRename() {
    isRenaming.value = false
    draftTitle.value = ''
}

function onMenuItem(kind) {
    menuOpen.value = false
    if (kind === 'publish') emit('publish')
    else if (kind === 'rename') startRename()
    else if (kind === 'delete') emit('delete-thread')
}

const vClickOutside = {
    mounted(el, binding) {
        el._clickOutside = (e) => {
            if (!el.contains(e.target)) binding.value && binding.value()
        }
        document.addEventListener('click', el._clickOutside)
    },
    unmounted(el) {
        if (el._clickOutside) document.removeEventListener('click', el._clickOutside)
    },
}
</script>

<style scoped>
.conversation-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 20px;
    background: white;
    border-bottom: 1px solid #e7e7e7;
    min-height: 44px;
}

.menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    cursor: pointer;
}

.menu-btn:hover {
    background: #f7f7f7;
}

.title-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
}

.title-icon {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #e7e7e7;
    color: #23282C;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.title-text {
    min-width: 0;
    flex: 1;
}

.title {
    font-size: 14px;
    font-weight: 600;
    color: #23282C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: text;
    border-bottom: 1px dashed transparent;
    transition: border-color 0.15s ease;
}

.title:hover {
    border-bottom-color: #c0c0c0;
}

.title-input {
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    color: #23282C;
    background: white;
    border: 1px solid #E30613;
    border-radius: 6px;
    padding: 4px 8px;
    width: 100%;
    max-width: 480px;
    outline: none;
}

.subtitle {
    font-size: 11px;
    color: #888888;
    margin-top: 1px;
}

.actions {
    display: flex;
    gap: 8px;
    align-items: center;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 12px;
    padding: 5px 10px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    cursor: pointer;
    transition: border-color 0.15s ease;
}

.action-btn:hover:not(:disabled) {
    border-color: #23282C;
}

.action-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    color: #888888;
}

.action-icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: white;
    color: #23282C;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.action-icon:hover {
    border-color: #23282C;
}

.more-wrap {
    position: relative;
}

.more-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 6px 20px rgba(21, 26, 34, 0.10);
    padding: 4px;
    min-width: 250px;
    z-index: 20;
}

.more-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 12.5px;
    color: #23282C;
    text-align: left;
    border-radius: 6px;
    cursor: pointer;
}

.more-item:hover {
    background: #f0f0f0;
}

.more-item--danger {
    color: #E30613;
}

.more-item--danger:hover {
    background: #FCEAEA;
}

.more-divider {
    height: 1px;
    background: #e7e7e7;
    margin: 4px 0;
}

.dd-enter-active,
.dd-leave-active {
    transition: opacity 0.14s ease, transform 0.14s ease;
}

.dd-enter-from,
.dd-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

@media (max-width: 768px) {
    .conversation-header {
        padding: 8px 12px;
        gap: 6px;
    }

    .action-btn {
        width: 32px;
        height: 32px;
        padding: 0;
        justify-content: center;
    }

    .action-label {
        display: none;
    }
}
</style>
